import React, { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import useStakeholderNotesFunctions from 'components/Dashboard/StakeHolders/StakeholderPage/useStakeholderNotesFunctions';
import {
  DeleteNotePopup,
  NotesInput,
  UserNote,
} from 'components/Dashboard/StakeHolders/StakeholderPage/ui/BioSections/Notes';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import { store } from 'components/Store';
import { useContext } from 'react';
import Skeleton from 'react-loading-skeleton';
import { AgencyLogoAndExplanation } from 'components/Dashboard/StakeHolders/StakeholderPage/ui/BioSections/NoteVisibility';
import time from 'utils/time';
import { Portal } from 'react-portal';

const StickyDateBadge = ({ children, index, stuckBadgesRef, onStuckChange, scrollContainerRef }) => {
  const [isStuck, setIsStuck] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const sentinelRef = useRef(null);
  const stickyRef = useRef(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const stuck = !entry.isIntersecting;
        setIsStuck(stuck);

        // Notify parent about stuck state change
        if (onStuckChange) {
          onStuckChange(index, stuck, stickyRef.current);
        }
      },
      {
        threshold: [1],
        rootMargin: '-1px 0px 0px 0px',
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [index, onStuckChange]);

  // Check if this badge is hidden behind another badge with higher z-index
  useEffect(() => {
    if (!isStuck || !stuckBadgesRef?.current || !stickyRef.current) {
      setIsHidden(false);
      return;
    }

    const checkIfHidden = () => {
      const currentRect = stickyRef.current?.getBoundingClientRect();
      if (!currentRect) return;

      // Check if any stuck badge with higher z-index overlaps with this one
      const isHiddenBehind = Object.entries(stuckBadgesRef.current).some(([badgeIndex, badgeData]) => {
        const badgeIdx = parseInt(badgeIndex);

        // Only check badges with higher z-index (higher index = higher z-index)
        if (badgeIdx <= index || !badgeData.isStuck || !badgeData.element) {
          return false;
        }

        const otherRect = badgeData.element.getBoundingClientRect();

        // Check if rectangles overlap vertically (they're at the same sticky position)
        const verticalOverlap = currentRect.top <= otherRect.bottom && currentRect.bottom >= otherRect.top;

        return verticalOverlap;
      });

      setIsHidden(isHiddenBehind);
    };

    checkIfHidden();

    // Recheck on scroll
    const scrollContainer = scrollContainerRef?.current?.view;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkIfHidden);
      return () => scrollContainer.removeEventListener('scroll', checkIfHidden);
    }
  }, [isStuck, index, stuckBadgesRef, scrollContainerRef]);

  return (
    <>
      {/* Sentinel element - invisible marker placed just before sticky element */}
      <div ref={sentinelRef} style={{ height: '1px', marginTop: '-1px' }} />

      <div
        ref={stickyRef}
        className={`d-flex align-items-center justify-content-center position-sticky specific-top-padding-1 notes-widget-date-badge top-${
          index === 0 ? 5 : 7
        } ${isHidden ? 'opacity-0' : ''}`}
        style={{
          zIndex: 10 + index,
          transition: 'all 0.1s',
        }}
      >
        {children}
      </div>
    </>
  );
};

const NotesWidget = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { team, activePlan } = state;

  const {
    openNotesWidgetState,
    heightContainer,
    referencePositionContainer,
    data,
    scrollbarRef: scrollbarRefProp,
  } = props;
  const { openNotesWidget, setOpenNotesWidget } = openNotesWidgetState ?? {};
  const { objectId, objectType: contentType } = data;

  const [value, setValue] = useState('');
  const [initialHeight, setInitialHeight] = useState(null);
  const [showToolbar, setShowToolbar] = useState(false);
  const [disabledTextArea, setDisabledTextArea] = useState(false);
  const [noteToDeleteId, setNoteToDeleteId] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [topValue, setTopValue] = useState(null);
  const [selectedVisibility, setSelectedVisibility] = useState('user');
  const [closeButtonPosition, setCloseButtonPosition] = useState({ top: 0, right: 0, opacity: 0 });

  const {
    isLoading,
    userNotes: notes,
    getNotes,
    addNote,
    updateNote,
    deleteNote,
    isNoteCreator,
  } = useStakeholderNotesFunctions();
  const { newAgencyPlan } = useTopicOrClientWord();
  const { user } = useAuth0();
  const isMobile = useIsMobile();

  const chatContainerRef = useRef(null);
  const scrollbarRef = useRef(null);
  const editorRef = useRef(null);
  const widgetContainer = useRef(null);
  const stuckBadgesRef = useRef({});

  useEffect(() => {
    if (widgetContainer.current && referencePositionContainer.current && topValue === null) {
      setTopValue(
        (widgetContainer?.current?.getBoundingClientRect()?.top ?? 0) -
          (referencePositionContainer?.current?.getBoundingClientRect()?.top ?? 0)
      );
    }
  }, [widgetContainer.current, referencePositionContainer.current]);

  useEffect(() => {
    if (widgetContainer.current && openNotesWidget && closeButtonPosition.opacity === 0) {
      setTimeout(() => {
        setCloseButtonPosition({
          top: widgetContainer.current?.getBoundingClientRect().top - 15,
          left: widgetContainer.current?.getBoundingClientRect().right - 15,
          opacity: 1,
        });
      }, 500);
    }
  }, [widgetContainer.current, openNotesWidget]);

  const initialCall = useRef(null);
  initialCall.current = async (source) => {
    await getNotes({ objectId, requestSource: source, contentType });
  };

  useEffect(() => {
    const source = new AbortController();
    initialCall.current(source);
    return () => {
      source.abort('Notes call cancelled by the user');
    };
  }, []);

  const smoothScrollToBottom = () => {
    if (scrollbarRef?.current) {
      scrollbarRef?.current?.view?.scrollTo({
        top: scrollbarRef?.current?.view?.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const createNoteController = async (e, editor = editorRef.current?.editor) => {
    const content = editor.getContent();
    const innerText = editor.getBody().innerText;
    if (((e.keyCode === 13 && !e.shiftKey) || e.type === 'click') && content !== '' && innerText.trim() !== '') {
      e.preventDefault();
      setDisabledTextArea(true);
      const addNoteProps = { objectId, text: content, contentType };
      if (newAgencyPlan) {
        addNoteProps.visibility = selectedVisibility;
      }
      await addNote(addNoteProps);
      setDisabledTextArea(false);
      editor.setContent('');
      setShowToolbar(false);
      setTimeout(() => {
        smoothScrollToBottom();
      }, 10);
    }
  };

  const toolbarVisibilityHandler = () => {
    if (editorRef.current?.editor?.focus) {
      editorRef.current.editor.focus();
      const body = editorRef.current.editor.getBody();
      if (body) {
        const caretSpans = body.querySelectorAll('span[id="_mce_caret"]');
        caretSpans.forEach((span) => {
          span.remove();
        });
      }
    }
    if (!openNotesWidget) {
      let headerDisplayed = true;
      const editorHeader = editorRef.current?.editor?.editorContainer?.querySelector('.tox-editor-header');
      if (editorHeader) {
        headerDisplayed = window.getComputedStyle(editorHeader).display !== 'none';
      }
      const refValue = showToolbar && headerDisplayed ? -26 : 26;
      const newHeight = editorRef.current?.editor?.container?.scrollHeight + refValue;
      editorRef.current.editor.container.style.height = `${newHeight}px`;
    }
    setShowToolbar(!showToolbar);
  };

  // Callback to track stuck state of each badge
  const handleStuckChange = (index, isStuck, element) => {
    stuckBadgesRef.current[index] = { isStuck, element };
  };

  const mainNotesInputProps = {
    editorRef,
    disabledTextArea,
    notes,
    showToolbar,
    createNoteController,
    newAgencyPlan,
    user,
    isMobile,
    value,
    setValue,
    contentType,
  };

  const currentDates = notes
    .sort((a, b) => new Date(a.created) - new Date(b.created))
    .map((item) => ({ id: item?.id, time: time(item?.created).format('DD MM YYYY') }));

  const showTimes = currentDates.map((item, idx, arr) => {
    return { id: item?.id, show: idx === 0 || item?.time !== arr[idx - 1]?.time };
  });

  return (
    <>
      <div
        ref={widgetContainer}
        className={`${
          openNotesWidget
            ? `dashboard-root-container pb-3 ${showTimes.filter((item) => item?.show).length <= 1 ? 'pt-3' : ''}`
            : 'mb-3 py-3'
        } box-rounded box-outstanding-reports bg-white ai-assistant-widget box-notes`}
        style={
          initialHeight
            ? {
                height: `${openNotesWidget ? heightContainer : initialHeight}px`,
                transform: `translateY(${openNotesWidget && !!topValue ? `-${topValue - 0.5}` : 0}px)`,
              }
            : null
        }
      >
        {!openNotesWidget && (
          <h3 className={`flex-centered justify-content-between px-3`}>
            <span>Add a note</span>{' '}
            <div className='d-flex align-items-center'>
              {newAgencyPlan && (
                <AgencyLogoAndExplanation
                  activeTab={'Notes'}
                  selectedVisibility={selectedVisibility}
                  setSelectedVisibility={setSelectedVisibility}
                  user={user}
                  positionX='left'
                  positionY='top'
                  scrollbarRef={scrollbarRefProp}
                />
              )}

              <span
                className='apply-note-formatting-button specific-x-padding-4 light-70-text light-blue-background-004-hover user-select-none d-block pointer'
                data-tooltip-content={isMobile ? '' : `${showToolbar ? 'Hide' : 'Show'} formatting`}
                data-tooltip-id='tooltip-mobile'
                data-tooltip-place='top'
                onClick={toolbarVisibilityHandler}
              >
                <span className={`d-inline-block ${showToolbar ? 'text-underline' : ''} font-weight-normal`}>Aa</span>
              </span>
            </div>
          </h3>
        )}
        {openNotesWidget && (
          <Portal node={document && document.querySelector('body')}>
            <button
              className='general-button circle-button bg-main-blue text-white z-max position-fixed transition-default'
              style={closeButtonPosition}
              onClick={() => {
                setOpenNotesWidget(false);
                setTimeout(() => {
                  setInitialHeight(null);
                }, 50);
              }}
            >
              <span className='icon-close paragraph-p5' />
            </button>
          </Portal>
        )}
        {openNotesWidget ? (
          <>
            <CustomScrollbar ref={scrollbarRef} maximalThumbYSize={100}>
              <div className='flex-grow-1 pb-3 mb-1' ref={chatContainerRef}>
                {notes
                  .sort((a, b) => new Date(a.created) - new Date(b.created))
                  .map((item, index) => {
                    const showTime =
                      showTimes.find((showItem) => showItem?.id === item?.id)?.show &&
                      showTimes.filter((showItem) => showItem?.show).length > 1;
                    return (
                      <React.Fragment key={`item-${item?.id}`}>
                        {showTime && (
                          <>
                            {index !== 0 && <div className='border-top notes-widget-date-separator'></div>}
                            <StickyDateBadge
                              index={index}
                              stuckBadgesRef={stuckBadgesRef}
                              onStuckChange={handleStuckChange}
                              scrollContainerRef={scrollbarRef}
                            >
                              <p
                                className={`mb-1 flex-centered text-main-blue-60 paragraph-p5 rounded bg-grey-100 px-2 specific-y-padding-1 border box-shadow mb-2`}
                                style={{ zIndex: 11 + index }}
                              >
                                <span>{time(item?.created).formatRelativeDay()}</span>
                              </p>
                            </StickyDateBadge>
                          </>
                        )}
                        <UserNote
                          key={`item-${item?.id}`}
                          item={item}
                          setNoteToDeleteId={setNoteToDeleteId}
                          setShowDeletePopup={setShowDeletePopup}
                          updateNote={updateNote}
                          isNoteCreator={isNoteCreator}
                          team={team}
                          activePlan={activePlan}
                          user={user}
                          newAgencyPlan={newAgencyPlan}
                          customNotesInputClass='content-related-notes-input'
                          className='pl-3 notes-widget-item'
                          showTime={'name'}
                        />
                      </React.Fragment>
                    );
                  })}
              </div>
            </CustomScrollbar>
            <div className='px-3'>
              <NotesInputContentRelated
                {...mainNotesInputProps}
                key={`${selectedVisibility}-notes-widget`}
                showToolbar={true}
                colorPickerPosition={'top'}
                selectedVisibility={selectedVisibility}
                setSelectedVisibility={setSelectedVisibility}
                scrollbarRefProp={scrollbarRefProp}
                openNotesWidget={openNotesWidget}
              />
            </div>
          </>
        ) : (
          <div className='px-3'>
            <NotesInputContentRelated {...mainNotesInputProps} key={`${selectedVisibility}-notes-widget`} />
            <div className='specific-top-padding-12'>
              {isLoading ? (
                <Skeleton height={20} width={80} />
              ) : (
                <>
                  {notes.length > 0 ? (
                    <span
                      className='paragraph-p2 text-light-blue pointer text-opacity-hover-75 transition-200'
                      onClick={() => {
                        setInitialHeight(widgetContainer?.current?.getBoundingClientRect()?.height);
                        setTimeout(() => {
                          setOpenNotesWidget(true);
                          if (editorRef.current?.editor) {
                            editorRef.current?.editor?.focus();
                          }
                        }, 50);
                        setTimeout(() => {
                          smoothScrollToBottom();
                        }, 500);
                      }}
                    >
                      See {notes.length} note{notes.length > 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span className='paragraph-p2 text-main-blue-65'>No notes from your team yet</span>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <DeleteNotePopup
        noteId={noteToDeleteId}
        setShowPopup={setShowDeletePopup}
        showPopUp={showDeletePopup && !!noteToDeleteId}
        deleteNote={deleteNote}
      />
    </>
  );
};

const AddNotesButton = ({ value, disabled, onClick }) => {
  return (
    <button
      className={`general-button bg-main-blue text-white assistant-widget-button flex-centered justify-content-center  ${
        disabled ? 'opacity-6 default-cursor' : ''
      }`}
      style={{ visibility: value?.trim() === '' ? 'hidden' : 'visible' }}
      onClick={(e) => {
        if (!disabled) {
          onClick(e);
        }
      }}
    >
      <span className={`icon-arrow-top paragraph-p5`} />
    </button>
  );
};

const NotesInputContentRelated = (props) => {
  const {
    editorRef,
    disabledTextArea,
    notes,
    showToolbar,
    createNoteController,
    newAgencyPlan,
    user,
    isMobile,
    value,
    setValue,
    onInitFunction,
    toolbarLocation,
    colorPickerPosition,
    selectedVisibility,
    setSelectedVisibility,
    scrollbarRefProp,
    openNotesWidget,
    contentType,
  } = props;
  return (
    <div
      className={`position-relative content-related-main-notes-input-container border d-flex align-items-start ${
        disabledTextArea ? 'notes-textarea-disabled' : ''
      }`}
    >
      <div className={`pr-1 left-6 ${toolbarLocation === 'top' ? 'bottom-8' : 'top-4'} position-absolute`}>
        <img
          style={{ width: '24px', minWidth: '24px' }}
          alt='avatar'
          className='rounded-circle img-fluid'
          src={`https://cdn.auth0.com/avatars/${Array.from(user?.given_name)[0].toLowerCase()}${Array.from(
            user?.family_name
          )[0].toLowerCase()}.png`}
        />
      </div>
      <NotesInput
        editorRef={editorRef}
        disabled={disabledTextArea}
        onChange={createNoteController}
        index={isMobile ? null : 0}
        notes={notes}
        showToolbar={showToolbar}
        customClass={`main-note-input ${
          newAgencyPlan ? 'main-note-input-agency' : ''
        } content-related-notes-input content-related-main-notes-input ${
          showToolbar ? 'content-related-main-note-input-toolbar-shown' : ''
        }`}
        value={value}
        setValue={setValue}
        onInitFunction={onInitFunction}
        minHeight={36}
        maxHeight={195}
        customStyle={`.mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
          color: #bababa !important;
          font-size: 0.925em !important;
          top: 2px;
        }
         body { 
          margin-top: -3px !important;
        }`}
        toolbarLocation={toolbarLocation}
        addVerticalScroll={true}
        colorPickerPosition={colorPickerPosition}
        placeholder={`Add a note about this ${contentType === 'CrmInteraction' ? 'interaction' : 'update'}`}
        disableAutofocus={!openNotesWidget}
      />
      {newAgencyPlan && openNotesWidget ? (
        <div className='position-absolute top-8 right-0'>
          <AgencyLogoAndExplanation
            activeTab={'Notes'}
            selectedVisibility={selectedVisibility}
            setSelectedVisibility={setSelectedVisibility}
            user={user}
            positionX='left'
            positionY='top'
            scrollbarRef={scrollbarRefProp}
            buttonClass='mr-0 border-0'
          />
        </div>
      ) : (
        <div className='position-absolute top-5 right-5'>
          <AddNotesButton value={value} disabled={disabledTextArea} onClick={createNoteController} />
        </div>
      )}
    </div>
  );
};

export default NotesWidget;
